import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { AppDispatch, useAppDispatch } from 'redux/store';
import { fetchNodeInstanceFile } from '../../../../redux/slices/node_instancesSlice';
import { SanitizedHtml } from 'components/SanitizedHtml/SanitizedHtml';
import './NodeInstanceFileViewer.scss';

/** The file body: the page's scroll owner (see NodeInstanceFileViewer.scss). */
const FILE_BODY_CLASS = 'file-viewer-body border rounded p-3 bg-light';

/** Pretty-prints a .json file; content that is not valid JSON (truncated, JSON Lines, a leading BOM) is shown as written. */
const formatJson = (content: string): string => {
  try {
    return JSON.stringify(JSON.parse(content), null, 2);
  } catch {
    return content;
  }
};

const NodeInstanceFileViewer: React.FC = () => {
  const { nodeInstanceId, fileName } = useParams<{
    nodeInstanceId: string;
    fileName: string;
  }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fileType, setFileType] = useState<string>('');

  useEffect(() => {
    const loadFile = async () => {
      if (!nodeInstanceId || !fileName) {
        setError('Missing node instance ID or file name');
        setIsLoading(false);
        return;
      }

      try {
        const result = await dispatch(
          fetchNodeInstanceFile({
            node_instance_id: parseInt(nodeInstanceId),
            file_name: fileName,
          })
        ).unwrap();

        setFileContent(result);
        setError(null);

        // Determine file type from extension
        const extension = fileName.split('.').pop()?.toLowerCase() || '';
        setFileType(extension);
      } catch (err: unknown) {
        // unwrap() rethrows the thunk's rejectValue: the server's message as a string.
        setError(typeof err === 'string' && err ? err : 'Failed to load file');
        setFileContent(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadFile();
  }, [dispatch, nodeInstanceId, fileName]);

  const handleClose = () => {
    window.close();
  };

  const renderFileContent = () => {
    if (!fileContent) return null;

    // Handle different file types
    switch (fileType) {
      case 'json':
        return (
          <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {formatJson(fileContent)}
          </pre>
        );
      case 'txt':
      case 'log':
      case 'md':
        return (
          <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {fileContent}
          </pre>
        );
      case 'csv':
        return (
          <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {fileContent}
          </pre>
        );
      default:
        // For unknown file types, try to display as text
        return (
          <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {fileContent}
          </pre>
        );
    }
  };

  const renderFileBody = () => {
    // Job-produced markup: SanitizedHtml sanitises it, so a crafted output file cannot run script in the viewer. Its box
    // is the file body itself. The component clips whatever overflows its box, so a wide report has to scroll in that
    // box, as every other file type scrolls in the body; inside a box of its own it would be cut off instead.
    if (fileContent && fileType === 'html') return <SanitizedHtml className={FILE_BODY_CLASS} html={fileContent} />;
    return <div className={FILE_BODY_CLASS}>{renderFileContent()}</div>;
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card m-3">
        <div className="card-body">
          <div className="text-danger mb-3">{error}</div>
          <button className="btn btn-primary" onClick={handleClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card m-3 file-viewer-shell">
      <div className="card-body">
        <div className="file-viewer-header d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">File: {fileName}</h4>
          <button className="btn btn-primary" onClick={handleClose}>
            Close
          </button>
        </div>
        {renderFileBody()}
      </div>
    </div>
  );
};

export default NodeInstanceFileViewer;
